import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef, DataTable } from '~/components/composites/data-table'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
import { useDebounce } from '~/hooks/use-debounce'
import { useQuery } from '~/hooks/use-query'
import { useAppStore } from '~/stores/app'
import type { AdminUser } from '~/types/admin-user'
import { DEFAULT_PAGE_SIZE, DialogId, type ListResponse } from '~/types/common'
import { QueryKey } from '~/types/query-key'
import { Role } from '~/types/user'

const ROLE_FILTER_ALL = 'all'

type UsersListProps = {
  onViewDetail: (user: AdminUser) => void
}

export function UsersList({ onViewDetail }: UsersListProps) {
  const { t } = useTranslation()
  const openDialog = useAppStore((state) => state.openDialog)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>(ROLE_FILTER_ALL)
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search)

  const queries: Record<string, unknown> = {
    page,
    limit: DEFAULT_PAGE_SIZE,
  }
  if (debouncedSearch) queries.search = debouncedSearch
  if (roleFilter !== ROLE_FILTER_ALL) queries.role = roleFilter

  const { data, isLoading } = useQuery<ListResponse<AdminUser>>({
    queryKey: QueryKey.ADMIN_USERS,
    queries,
  })

  const handleView = (user: AdminUser) => {
    onViewDetail(user)
    openDialog(DialogId.DETAIL_USER)
  }

  const columns: ColumnDef<AdminUser>[] = [
    {
      key: 'name',
      header: t('users.name'),
      render: (user) => (
        <div>
          <p className="font-medium">{user.profile?.name ?? user.username}</p>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      ),
    },
    {
      key: 'phone',
      header: t('users.phone'),
      render: (user) => user.phone ?? '-',
    },
    {
      key: 'role',
      header: t('users.role'),
      render: (user) => (
        <Badge variant={user.role === Role.ADMIN ? 'default' : 'secondary'}>
          {user.role === Role.ADMIN ? t('users.roleAdmin') : t('users.roleUser')}
        </Badge>
      ),
      className: 'w-[110px]',
    },
    {
      key: 'pointBalance',
      header: t('users.pointBalance'),
      render: (user) => user.pointBalance.toLocaleString(),
      className: 'w-[160px]',
    },
    {
      key: 'phoneVerified',
      header: t('users.phoneVerified'),
      render: (user) => (
        <Badge variant={user.phoneVerified ? 'success' : 'secondary'}>
          {user.phoneVerified ? t('common.yes') : t('common.no')}
        </Badge>
      ),
      className: 'w-[140px]',
    },
    {
      key: 'emailVerified',
      header: t('users.emailVerified'),
      render: (user) => (
        <Badge variant={user.emailVerified ? 'success' : 'secondary'}>
          {user.emailVerified ? t('common.yes') : t('common.no')}
        </Badge>
      ),
      className: 'w-[140px]',
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      render: (user) => new Date(user.createdAt).toLocaleDateString(),
      className: 'w-[150px]',
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (user) => (
        <Button variant="outline" size="sm" onClick={() => handleView(user)}>
          {t('users.view')}
        </Button>
      ),
      className: 'w-[100px]',
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          id="users-search"
          placeholder={t('users.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="max-w-sm"
        />
        <Select
          value={roleFilter}
          onValueChange={(value) => {
            setRoleFilter(value)
            setPage(1)
          }}
        >
          <SelectTrigger id="users-role-filter" className="w-[180px]">
            <SelectValue placeholder={t('users.filterRole')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ROLE_FILTER_ALL}>{t('users.filterAllRoles')}</SelectItem>
            <SelectItem value={Role.ADMIN}>{t('users.roleAdmin')}</SelectItem>
            <SelectItem value={Role.USER}>{t('users.roleUser')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pagination={data?.pagination}
        onPageChange={setPage}
      />
    </div>
  )
}
